import keycloakClient from "@keycloak/keycloak";
import { Source } from "@services/sourceManagerApi/models/Source";
import { SOURCE_MANAGER_API_URL } from "../../config";
import {
  GetAllSourcesListRequest,
  SourceStatusResponse,
  UpdateSourceCategoriesRequest,
  UploadSourceResponse,
  UploadSourcesRequest
} from "./apiRequestsModels";

class SourceManagerApi {
  private baseUrl: string;

  allowedExtensions = ["pdf", "docx", "xlsx", "xls", "csv"];

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(includeContentType: boolean = true): HeadersInit {
    const headers: HeadersInit = {
      Authorization: `Bearer ${keycloakClient.token}`,
      Accept: "application/json"
    };
    if (includeContentType) {
      headers["Content-Type"] = "application/json";
    }
    return headers;
  }

  private handleApiError(error: any): never {
    console.error("Error en la comunicación con el API de fuentes:", error);
    throw error;
  }

  async uploadSources(
    data: UploadSourcesRequest
  ): Promise<UploadSourceResponse[]> {
    try {
      const formData = new FormData();

      data.role_ids.forEach((role_id: number) => {
        formData.append("role_ids", role_id.toString());
      });

      data.files.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(`${this.baseUrl}/sources`, {
        method: "POST",
        headers: this.getAuthHeaders(false),
        body: formData
      });

      if (!response.ok) {
        throw new Error(
          `Error al subir las fuentes: ${response.status} ${response.statusText}`
        );
      }

      const responseData = (await response.json()) as UploadSourceResponse[];
      return responseData;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async getAllSources(): Promise<Source[]> {
    try {
      const response = await fetch(`${this.baseUrl}/sources`, {
        method: "GET",
        headers: this.getAuthHeaders(true)
      });

      if (!response.ok) {
        throw new Error(
          `Error al obtener las fuentes: ${response.status} ${response.statusText}`
        );
      }
      const responseData = (await response.json()) as GetAllSourcesListRequest;
      return responseData.sources;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async getSourcesById(sourceId: number): Promise<Source> {
    try {
      const response = await fetch(`${this.baseUrl}/sources/${sourceId}`, {
        method: "GET",
        headers: this.getAuthHeaders(true)
      });

      if (!response.ok) {
        throw new Error(`Error when trying to get source with id ${sourceId}`);
      }
      const responseData = (await response.json()) as Source;
      return responseData;
    } catch (e) {
      this.handleApiError(e);
    }
  }

  async getSourcesFilteredByCategoryId(
    categoryIds: number[]
  ): Promise<Source[]> {
    try {
      const queryParams = categoryIds
        .map((id) => `category_id=${id}`)
        .join("&");
      const response = await fetch(`${this.baseUrl}/sources?${queryParams}`, {
        method: "GET",
        headers: this.getAuthHeaders(true)
      });

      if (!response.ok) {
        throw new Error(
          `Error al obtener las fuentes: ${response.status} ${response.statusText}`
        );
      }
      const responseData = (await response.json()) as GetAllSourcesListRequest;
      return responseData.sources;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async getSourcesStatus(sourceIds: number[]): Promise<SourceStatusResponse[]> {
    try {
      const queryParams = sourceIds.map((id) => `id=${id}`).join("&");
      const response = await fetch(
        `${this.baseUrl}/sources/status?${queryParams}`,
        {
          method: "GET",
          headers: this.getAuthHeaders(true)
        }
      );

      if (!response.ok) {
        throw new Error(`Error al obtener el estado de las fuentes`);
      }

      const responseData = (await response.json()) as SourceStatusResponse[];
      return responseData;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async deleteSource(source_id: number): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/sources/${source_id}`, {
        method: "DELETE",
        headers: this.getAuthHeaders(true)
      });

      if (!response.ok) {
        throw new Error(
          `Error al eliminar las fuentes: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }

  async updateSourceCategories(
    data: UpdateSourceCategoriesRequest
  ): Promise<boolean> {
    try {
      const formData = new FormData();

      data.categories_ids.forEach((role_id) => {
        formData.append("categories_ids", role_id.toString());
      });

      const response = await fetch(
        `${this.baseUrl}/sources/${data.source_id}/categories`,
        {
          method: "PATCH",
          headers: this.getAuthHeaders(false),
          body: formData
        }
      );
      if (!response.ok) {
        throw new Error(
          `Error al actualizar categorías del source: ${response.status} ${response.statusText}`
        );
      }
      return true;
    } catch (error) {
      this.handleApiError(error);
    }
  }
}

const PREFIX = "chatbot-source-manager-api/v1";

export const sourceManagerApiService = new SourceManagerApi(
  `${SOURCE_MANAGER_API_URL}/${PREFIX}`
);
